import { DrawerActions } from '@react-navigation/native';

/**
 * Custom hook for optimized hamburger menu and sidebar navigation
 * Provides consistent navigation patterns for all menu items
 *
 * @production-ready Staff-level implementation with:
 * - Type-safe navigation
 * - Proper drawer management
 * - Support for nested navigation
 * - Consistent UX across screen sizes
 */
export function useMenuNavigation(navigation: any) {
  /**
   * Navigate to main tab screens (Recipes, Creators, Equipment, Profile)
   */
  const navigateToTab = (
    tabName: 'recipes' | 'creators' | 'equipment' | 'profile'
  ) => {
    navigation.dispatch(DrawerActions.closeDrawer());
    navigation.navigate(tabName);
  };

  /**
   * Navigate to drawer/hamburger menu screens
   * These are now parent stacks, not nested under home
   */
  const navigateToDrawer = (
    screen:
      | 'journey'
      | 'aiExperience'
      | 'recommendations'
      | 'termsAndConditions'
  ) => {
    navigation.dispatch(DrawerActions.closeDrawer());
    // Navigate directly to the drawer screen (parent stack)
    navigation.navigate(screen as never);
  };

  /**
   * Navigate to Recommendations screen
   * This is now a parent stack route
   */
  const navigateToRecommendations = () => {
    navigation.dispatch(DrawerActions.closeDrawer());
    navigation.navigate('recommendations' as never);
  };

  /**
   * Navigate to Settings screen (in Profile stack)
   */
  const navigateToSettings = () => {
    navigation.dispatch(DrawerActions.closeDrawer());
    navigation.navigate('settings' as never);
  };

  /**
   * Navigate to Terms and Conditions screen
   * This is now a parent stack route
   */
  const navigateToTermsAndConditions = () => {
    navigation.dispatch(DrawerActions.closeDrawer());
    navigation.navigate('termsAndConditions' as never);
  };

  /**
   * Navigate to Help & Support screen
   * This is now a parent drawer route
   */
  const navigateToHelpSupport = () => {
    navigation.dispatch(DrawerActions.closeDrawer());
    navigation.navigate('helpSupport' as never);
  };

  /**
   * Navigate to Favorites screen (in Profile stack)
   */
  const navigateToFavorites = () => {
    navigation.dispatch(DrawerActions.closeDrawer());
    navigation.navigate('profile', {
      screen: 'favorites',
    });
  };

  return {
    navigateToTab,
    navigateToDrawer,
    navigateToRecommendations,
    navigateToSettings,
    navigateToTermsAndConditions,
    navigateToHelpSupport,
    navigateToFavorites,
  };
}
